import { HeartHandshake, RefreshCw } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { useCompanyTenets, useSyncCompanyTenets } from "@/hooks/use-company-tenets";

/**
 * Company tenets — what an endorsement on a review says somebody embodies.
 *
 * Read-only. The list is the legacy Feedback app's "Endorsement Tags", copied
 * in by the refresh button; it is changed there, not here. Retired tags stay
 * listed because endorsements already given may name them.
 */
export function SettingsCompanyTenetsPage() {
  const { data: tenets = [], isLoading, isError, error, refetch } = useCompanyTenets();
  const sync = useSyncCompanyTenets();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load company tenets"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The values a reviewer can say someone embodies. These appear on every review, whatever the
        person's role, and each endorsement has to say why. They come from the Endorsement Tags in
        the Feedback app — change them there, then refresh.
      </p>

      <DataToolbar
        actions={
          <Button variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw className={`mr-2 h-4 w-4 ${sync.isPending ? "animate-spin" : ""}`} />
            {sync.isPending ? "Refreshing…" : "Refresh from Feedback app"}
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Name", className: "w-56" },
          { label: "Description" },
          { label: "Status", className: "w-28" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={HeartHandshake}
            title="No tenets yet"
            description="Refresh from the Feedback app to bring them in. Until then, reviewers have nothing to endorse."
          />
        }
      >
        {tenets.map((tenet) => (
          <tr key={tenet.id} className="border-b border-border last:border-0 align-top">
            <td className="px-4 py-3 font-medium">{tenet.name}</td>
            <td className="px-4 py-3 text-muted-foreground whitespace-pre-line">
              {tenet.description || "—"}
            </td>
            <td className="px-4 py-3">
              <StatusPill
                tone={tenet.isActive ? "success" : "neutral"}
                label={tenet.isActive ? "In use" : "Retired"}
              />
            </td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}
